import { Injectable } from '@angular/core'
import {
  Observable,
  combineLatest,
  concatMap,
  defaultIfEmpty,
  firstValueFrom,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import {
  PackingList,
  PackingListAction,
  PackingListAuthData,
  PackingListOrderData,
  PackingListPackData,
  PackingListPickData,
  PackingListStationData,
  PackingListsListingData,
  PackingListsListingPage,
  PackingScanEvent,
} from './models/packing-list.entity'
import { Page } from '../../models/util.model'
import { parsePackingListPageKeys } from './libs/packing-search.lib'
import { UsersService, User } from '../users'
import { PackingListsService } from './packing-lists.service'
import { Warehouse, WarehousesService } from '../warehouses'
import {
  PickingList,
  PickingListsService,
  checkPickingOrdersPackable,
  getPickingPickedByUser,
  getPickingStartingToteId,
} from '../picking-lists'
import { Location, LocationSearchParams, LocationsService } from '../locations'
import { LocalStorage } from '../storage'
import {
  Product,
  ProductStatus,
  ProductsService,
  getProductBarcodeQty,
  getProductWrapperByBarcode,
} from '../products'
import {
  Order,
  OrderAdditionalPackage,
  OrderPackage,
  OrdersService,
  addOrderProductPackage,
  addOrderWrapperPackage,
} from '../orders'
import {
  getPackingActionToPack,
  getPackingOrderToPack,
} from './libs/packing-orders.lib'
import { getPackingActionWeightToPack } from './libs/packing-products.lib'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  PackingListModalCode,
  PackingListNotificationCode,
  parsePackingListModal,
  parsePackingListNotification,
} from './libs/packing-notification.lib'
import {
  PackingListField,
  PackingListSearchParams,
} from './models/packing-list-search.model'
import { PrintingType } from './models/packing-list.types'
import { uniqBy } from 'lodash'

const PACK_WAREHOUSE_ID = 'pk_whd'
const PACK_PACKAGE_LOCATION_ID = 'pk_pkld'
const PACK_COMPUTER_ID = 'pk_cpd'
const PACK_PRINTER_ID = 'pk_prd'
const PACK_PRINTER_A4_ID = 'pk_pr4d'

@Injectable({
  providedIn: 'root',
})
export class PackingListsRepository {
  constructor(
    private packingListsService: PackingListsService,
    private warehousesService: WarehousesService,
    private pickingListsService: PickingListsService,
    private productsService: ProductsService,
    private ordersService: OrdersService,
    private usersService: UsersService,
    private locationsService: LocationsService,
    private storage: LocalStorage,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchPackingLists$(
    searchParams: PackingListSearchParams,
    fields: PackingListField[],
    listingData?: PackingListsListingData,
    returnAll?: boolean,
  ): Observable<PackingListsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.packingListsService.limit

    return needsPagination
      ? this._paginatePackingLists$(searchParams, fields, listingData)
      : this._searchPackingLists$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<PackingListsListingPage> for search goods-returns
   */
  private _searchPackingLists$(
    searchParams: PackingListSearchParams,
    fields: PackingListField[],
    listingData?: PackingListsListingData,
    returnAll?: boolean,
  ): Observable<PackingListsListingPage> {
    return this.packingListsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<PackingListsListingPage> for search goods-returns
   */
  private _paginatePackingLists$(
    searchParams: PackingListSearchParams,
    fields: PackingListField[],
    listingData?: PackingListsListingData,
  ): Observable<PackingListsListingPage> {
    const totalCount = searchParams.limit || this.packingListsService.limit
    const pagesCount = Math.ceil(totalCount / this.packingListsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.packingListsService.limit,
        offset: i * this.packingListsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchPackingLists$(pageParams, fields, listingData),
      ),
      scan<PackingListsListingPage, PackingListsListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as PackingListsListingPage,
      ),
      last(),
    )
  }

  private _loadExtData$(
    page: Page<PackingList>,
    fields: PackingListField[],
    listingData?: PackingListsListingData,
  ): Observable<PackingListsListingData> {
    const extDataKeys = parsePackingListPageKeys(page)
    const obs$: Partial<{
      users: Observable<User[]>
      warehouses: Observable<Warehouse[]>
      pickingLists: Observable<PickingList[]>
    }> = {}

    if (
      (fields.includes('startedBy') ||
        fields.includes('packedBy') ||
        fields.includes('startedBy')) &&
      extDataKeys.userIds
    ) {
      const existingUsers = listingData?.users || []
      const hasAllUsers = extDataKeys.userIds.every((id) =>
        existingUsers.some((user) => user._id === id),
      )

      if (!hasAllUsers) {
        obs$.users = this.usersService.store$(
          extDataKeys.userIds,
          existingUsers,
        )
      } else {
        obs$.users = of(existingUsers)
      }
    }

    if (fields.includes('warehouseId') && extDataKeys.warehouseIds) {
      const existingWarehouses = listingData?.warehouses || []
      const hasAllWarehouses = extDataKeys.warehouseIds.every((id) =>
        existingWarehouses.some((warehouse) => warehouse._id === id),
      )

      if (!hasAllWarehouses) {
        obs$.warehouses = this.warehousesService.store$(
          extDataKeys.warehouseIds,
          existingWarehouses,
        )
      } else {
        obs$.warehouses = of(existingWarehouses)
      }
    }

    if (fields.includes('pickingListId') && extDataKeys.pickingListIds) {
      if (!listingData?.pickingLists || listingData.pickingLists.length === 0) {
        obs$.pickingLists = this.pickingListsService.store$(
          extDataKeys.pickingListIds,
          listingData?.pickingLists || [],
        )
      } else {
        obs$.pickingLists = of(listingData.pickingLists)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as PackingListsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: PackingListsListingData = {}
        if (results.users) extData.users = results.users
        if (results.warehouses) extData.warehouses = results.warehouses
        if (results.pickingLists) extData.pickingLists = results.pickingLists
        return extData
      }),
    )
  }

  /**
   * Load pick data by packing-list ID
   * @param packingListId - the packing-list ID
   * @returns the observable for load packing-list pick data
   */
  loadPackingDataById$(packingListId: string): Observable<PackingListPickData> {
    return this.packingListsService
      .read$(packingListId)
      .pipe(
        switchMap((packingList) =>
          this.loadPackingPickDataByPickingId$(packingList.pickingListId),
        ),
      )
  }

  /**
   * Load pick data by picking-list ID
   * @param pickingListId - the picking list ID
   * @returns the observable for load packing data by picking-list ID
   */
  async loadPackingDataByPickingId(
    pickingListId: string,
  ): Promise<string | undefined> {
    const packingList = await firstValueFrom(
      this.packingListsService
        .findOne$({ pickingListId })
        .pipe(defaultIfEmpty(undefined)),
    )
    return packingList?._id
  }

  /**
   * Load pick data by tote CODE
   * @param code - the location tote code
   * @param warehouseId - the warehouse ID to specify
   * @returns the observable for load packing-list pick data
   */
  async loadPackingDataByToteCode(
    code: string,
    warehouseId?: string,
  ): Promise<string | undefined> {
    const params: LocationSearchParams = { code }

    if (warehouseId) {
      params.warehouseId = warehouseId
    }

    const totes = await firstValueFrom(
      this.locationsService.list$(params).pipe(defaultIfEmpty(undefined)),
    )

    if (!totes || totes.length < 1) {
      this.notify({ code: 'TOTE_NOT_FOUND' })
      return
    }
    const tote = totes[0]

    if (!tote.pickingListId) {
      this.notify({ code: 'TOTE_EMPTY' })
      return
    }

    return tote.pickingListId
  }

  /**
   * Load packing data by picking-list ID
   * @param pickingListId - the picking-list ID
   * @param packingList - the packing-list already loaded
   * @returns the observable for load packing data
   */
  loadPackingPickDataByPickingId$(
    pickingListId: string,
    packingList?: PackingList,
  ): Observable<PackingListPickData> {
    return this.pickingListsService
      .read$(pickingListId)
      .pipe(
        switchMap((pickingList) =>
          this.loadPackingPickDataByPicking$(pickingList, packingList),
        ),
      )
  }

  /**
   * Load packing station data
   * @returns the observable for load station data
   */
  loadPackingStationData$(
    authData?: PackingListAuthData,
  ): Observable<PackingListStationData> {
    // Get storage data
    const warehouseId =
      this.storage.get(PACK_WAREHOUSE_ID) || authData?.warehouse?._id
    const packLocationId =
      this.storage.get(PACK_PACKAGE_LOCATION_ID) ||
      authData?.settings?.packagingLocationId ||
      authData?.warehouse?.packagingLocationId
    const computerId =
      this.storage.get(PACK_COMPUTER_ID) || authData?.settings?.computerId
    const printerId =
      this.storage.get(PACK_PRINTER_ID) || authData?.settings?.printers?.label
    const A4PrinterId =
      this.storage.get(PACK_PRINTER_A4_ID) || authData?.settings?.printers?.a4

    return combineLatest({
      warehouseId: warehouseId
        ? of(warehouseId)
        : this.warehousesService
            .list$({ limit: 1 })
            .pipe(
              map((warehouses) =>
                warehouses.length ? warehouses[0]._id : undefined,
              ),
            ),
      packagingLocation: packLocationId
        ? this.locationsService.read$(packLocationId)
        : of(undefined),
      currentUserId: of(authData?.user?._id),
      packagingManagement: this.productsService.isPackagingManagementEnabled$(),
      printingType: of('manual' as PrintingType),
      computerId: of(computerId ? computerId.toString() : undefined),
      printerId: of(printerId ? printerId.toString() : undefined),
      A4PrinterId: of(A4PrinterId ? A4PrinterId.toString() : undefined),
    })
  }

  /**
   * Save packing station data
   * @param stationData - the packing list station data
   */
  savePackingStationData(stationData: PackingListStationData): void {
    const {
      warehouseId,
      packagingLocation,
      computerId,
      printerId,
      A4PrinterId,
    } = stationData

    warehouseId
      ? this.storage.set(PACK_WAREHOUSE_ID, warehouseId)
      : this.storage.remove(PACK_WAREHOUSE_ID)

    packagingLocation
      ? this.storage.set(PACK_PACKAGE_LOCATION_ID, packagingLocation._id)
      : this.storage.remove(PACK_PACKAGE_LOCATION_ID)

    computerId
      ? this.storage.set(PACK_COMPUTER_ID, computerId.toString())
      : this.storage.remove(PACK_COMPUTER_ID)

    printerId
      ? this.storage.set(PACK_PRINTER_ID, printerId.toString())
      : this.storage.remove(PACK_PRINTER_ID)

    A4PrinterId
      ? this.storage.set(PACK_PRINTER_A4_ID, A4PrinterId.toString())
      : this.storage.remove(PACK_PRINTER_A4_ID)
  }

  /**
   * Load packing data by picking-list
   * @param pickingList - the picking-list
   * @param packingList - the packing-list already loaded
   * @returns the observable for load packing data
   */
  loadPackingPickDataByPicking$(
    pickingList: PickingList,
    packingList?: PackingList,
  ): Observable<PackingListPickData> {
    return combineLatest({
      pickingList: of(pickingList),
      packingList: packingList
        ? of(packingList)
        : this.packingListsService
            .list$({
              pickingListId: pickingList._id,
            })
            .pipe(map((packings) => packings[0])),
      pickingUser: this.loadPickingUser$(pickingList),
      orders: this.loadPickingOrders$(pickingList),
      totes: this.loadPickingTotes$(pickingList),
    })
  }

  /**
   * Start a packing-list from a picking-list
   * @param pickingList - the picking-list
   * @param printers - the printers configuration
   * @param packagingLocationId - the packaging location ID
   * @returns the observable for start a new packing-list
   */
  startPacking$(
    pickingList: PickingList,
    orders: Order[],
    printerId: string,
    printerA4Id?: string,
    packagingLocationId?: string,
  ): Observable<PackingListPackData | undefined> {
    return this.checkPickingPackable$(pickingList).pipe(
      switchMap((toteId) =>
        toteId
          ? this.packingListsService
              .create$(
                toteId,
                { labels: printerId, a4: printerA4Id ?? '' },
                packagingLocationId,
              )
              .pipe(
                switchMap((packingList) =>
                  this.loadPackingPackData$(packingList, orders),
                ),
              )
          : of(undefined),
      ),
    )
  }

  /**
   * Restore a packing-list already started
   * @param packingList - the packing-list to restore
   * @param pickingList - the picking-list
   * @param currentUserId - the current user ID
   * @param alertComponent - the alert component to use
   * @returns the observable for restore the packing
   */
  async restorePacking$(
    packingList: PackingList,
    pickingList: PickingList,
    orders: Order[],
    currentUserId: string,
    printerId: string,
    printerA4Id?: string,
    packagingLocationId?: string,
  ): Promise<PackingListPackData | undefined> {
    if (currentUserId !== packingList.userId) {
      const user = await firstValueFrom(
        this.usersService
          .findOne$({ _id: packingList.userId })
          .pipe(defaultIfEmpty(undefined)),
      )
      const response = await this.alert('PACKED_BY_ANOTHER_USER', {
        pickingListName: pickingList.name,
        user,
      })
      if (response === 'success') {
        return await firstValueFrom(
          this.takeUpPacking$(
            packingList._id,
            orders,
            printerId,
            printerA4Id ?? '',
            packagingLocationId,
          ).pipe(defaultIfEmpty(undefined)),
        )
      }
    }

    const takeUp = await firstValueFrom(
      this.packingListsService
        .takeUp$(
          packingList._id,
          { labels: printerId, a4: printerA4Id ?? '' },
          packagingLocationId,
        )
        .pipe(defaultIfEmpty(undefined)),
    )
    return takeUp
      ? await firstValueFrom(
          this.loadPackingPackData$(takeUp, orders).pipe(
            defaultIfEmpty(undefined),
          ),
        )
      : undefined
  }

  /**
   * Take-up packing started by another user
   * @param packingListId - the packing-list ID
   * @param orders - the orders of the packing
   * @param printerId - the printer ID
   * @param printerA4Id - the A4 printer ID
   * @param packagingLocationId - the packaging location ID
   * @returns the observable for take-up the packing
   */
  takeUpPacking$(
    packingListId: string,
    orders: Order[],
    printerId: string,
    printerA4Id?: string,
    packagingLocationId?: string,
  ): Observable<PackingListPackData> {
    return this.packingListsService
      .takeUp$(
        packingListId,
        { labels: printerId, a4: printerA4Id ?? '' },
        packagingLocationId,
      )
      .pipe(
        switchMap((packingList) =>
          this.loadPackingPackData$(packingList, orders),
        ),
      )
  }

  /**
   * Load packing pack data
   * @param packingList - the packing list
   * @param orders - the orders of the packing
   * @returns the observable for load packing pack data
   */
  loadPackingPackData$(
    packingList: PackingList,
    orders: Order[],
    lastOrder?: Order,
  ): Observable<PackingListPackData> {
    const orderToPack = getPackingOrderToPack(packingList)
    const order = orders.find((o) => o._id === orderToPack?._id)

    if (!order) {
      return of({
        packingList,
        lastOrder,
      })
    }

    return this.loadOrderData$(packingList, order).pipe(
      map((orderData) => ({
        packingList,
        lastOrder,
        ...orderData,
      })),
    )
  }

  /**
   * Load order packing data
   * @param packingList - the packing list,
   * @param order - the order
   * @returns the observable for load the packing order data
   */
  loadOrderData$(
    packingList: PackingList,
    order: Order,
  ): Observable<PackingListOrderData> {
    return combineLatest({
      order: of(order),
      action: of(getPackingActionToPack(packingList, order._id)),
      products: this.loadPackingOrderProducts$(packingList, order._id),
      packages: of(order.packages),
      additionalPackages: of(order.additionalPackages),
    })
  }

  /**
   * Check if a picking is packable and notify the user if not
   * @param pickingList - the picking list to check
   * @returns observable<boolean> to check if the picking is packable
   */
  checkPickingPackable$(
    pickingList: PickingList,
  ): Observable<string | undefined> {
    return this.loadPickingOrders$(pickingList).pipe(
      map((orders) => checkPickingOrdersPackable(orders)),
      switchMap((ordersPackable) => {
        if (!ordersPackable) {
          this.notify({ code: 'PICKING_NOT_PACKABLE' })
          return of(undefined)
        }

        const toteId = getPickingStartingToteId(pickingList)
        if (!toteId) {
          this.notify({ code: 'PICKING_WITHOUT_TOTES' })
          return of(undefined)
        }

        return of(toteId)
      }),
    )
  }

  /**
   * Scan a product of a packing-list
   * @param productCode - the scanned CODE
   * @param packingData - the packing-list pack data
   * @param orderData - the packing-list order data
   * @returns the observable for scan a product
   */
  scanProduct$(
    scannedCode: string,
    packingData: PackingListPickData,
    orderData: PackingListOrderData,
  ): Observable<PackingListPackData | undefined> {
    return this.productsService.list$({ code: scannedCode }).pipe(
      switchMap((products) => {
        const product: Product | undefined = products.length
          ? products[0]
          : undefined
        if (!product) {
          this.notify({ code: 'PRODUCT_NOT_FOUND' })
          return of(undefined)
        }

        if (!packingData.packingList || !packingData.orders) {
          this.notify({ code: 'MISSING_PACKING_DATA' })
          return of(undefined)
        }

        const { packingList, orders } = packingData

        // Check product package
        if (product.productType === 'PACKAGE') {
          return this.addPackage$(product, packingList, orderData)
        }

        return this.packProduct$(
          product,
          packingList,
          orders,
          orderData,
          scannedCode,
          getProductBarcodeQty(product, scannedCode),
        )
      }),
    )
  }

  /**
   * Add a package to pack data
   * @param product - the product package
   * @param packData - the pack data
   * @returns the observable for adding a package to packing packages
   */
  addPackage$(
    product: Product,
    packingList: PackingList,
    orderData: PackingListOrderData,
  ): Observable<PackingListPackData | undefined> {
    if (product.status === ProductStatus.disabled) {
      this.notify({ code: 'PACKAGE_DISABLED' })
      return of(undefined)
    }

    if (!orderData.order) {
      this.notify({ code: 'ORDER_NOT_SELECTED' })
      return of(undefined)
    }

    const weightToPack = getPackingActionWeightToPack(
      packingList,
      orderData.order._id,
      orderData.packages || [],
    )

    const order = orderData.order
    return of({
      ...orderData,
      packingList,
      packages: addOrderProductPackage(
        orderData.packages || [],
        product,
        weightToPack,
      ),
      scanEvents: [
        {
          phase: 'PACKAGE_ADDITION',
          productId: product._id,
          qty: 1,
          orderId: order._id,
          tenantId: order.tenantId,
        },
      ],
    })
  }

  /**
   * Pack a product
   * @param product - the product to pack
   * @param packingList - the packing-list
   * @param orders - the orders of the packing-list
   * @param packData - the pack data
   * @param quantity - the quantity to pack
   * @returns the observable for pack a product
   */
  packProduct$(
    product: Product,
    packingList: PackingList,
    orders: Order[],
    packData: PackingListOrderData,
    barcode: string,
    quantity = 1,
  ): Observable<PackingListPackData | undefined> {
    // Check current order
    let action: PackingListAction | undefined
    let obs$: Observable<{ order: Order; products: Product[] }>

    const prodActions = packingList.actions.filter(
      (a) => a.productId === product._id,
    )

    if (!prodActions.length) {
      this.notify({ code: 'PRODUCT_PACK_NOT_CONTAINED' })
      return of(undefined)
    }

    if (packData.order && packData.products) {
      const orderActions = prodActions.filter(
        (a) => a.orderId === packData.order?._id,
      )
      if (!orderActions.length) {
        this.notify({
          code: 'PRODUCT_ORDER_NOT_CONTAINED',
        })
        return of(undefined)
      }

      action = orderActions.find((a) => a.qtyToPack !== a.qtyPacked)
      if (!action) {
        this.notify({ code: 'PRODUCT_ALREADY_PACKED' })
        return of(undefined)
      }

      obs$ = combineLatest({
        order: of(packData.order),
        products: of(packData.products),
      })
    } else {
      action = prodActions.find((a) => a.qtyToPack !== a.qtyPacked)
      if (!action) {
        this.notify({ code: 'PRODUCT_ALREADY_PACKED' })
        return of(undefined)
      }

      const order = orders.find((o) => o._id === action?.orderId)
      if (!order) {
        this.notify({ code: 'ORDER_NOT_FOUND' })
        return of(undefined)
      }

      obs$ = combineLatest({
        order: of(order),
        products: this.loadPackingOrderProducts$(packingList, order._id),
      })
    }

    return obs$.pipe(
      switchMap(({ order, products }) =>
        this.packingListsService
          .scanProduct$(packingList._id, order._id, {
            productId: product._id,
            quantity,
            lot: action?.lot,
            expirationDate: action?.expirationDate,
            serial: action?.serial,
          })
          .pipe(
            map((updatedPacking) => {
              // Check action
              let nextAction = updatedPacking.actions.find(
                (a) => a.productId === product._id && a.orderId === order._id,
              )
              if (nextAction?.qtyToPack === nextAction?.qtyPacked) {
                nextAction = getPackingActionToPack(updatedPacking, order._id)
              }

              const packages = this.checkProductPackages(
                updatedPacking,
                product,
                barcode,
                packData.packages || [],
                order._id,
              )

              const scanEvents: PackingScanEvent[] = [
                {
                  phase: 'PRODUCT_CONFIRMATION',
                  productId: product._id,
                  qty: quantity,
                  orderId: order._id,
                  tenantId: order.tenantId,
                  lot: action?.lot,
                  serial: action?.serial,
                  expirationDate: action?.expirationDate
                    ? new Date(action.expirationDate).toISOString()
                    : undefined,
                },
              ]

              if (packages.length > (packData.packages?.length ?? 0)) {
                const added = packages[packages.length - 1]
                scanEvents.push({
                  ...(added.packageId ? { productId: added.packageId } : {}),
                  phase: 'PACKAGE_ADDITION',
                  productId: added.packageId ?? '-',
                  qty: 1,
                  orderId: order._id,
                  tenantId: order.tenantId,
                })
              }

              return {
                ...packData,
                packingList: updatedPacking,
                packages,
                action: nextAction,
                order,
                products,
                scanEvents,
              }
            }),
          ),
      ),
    )
  }

  /**
   * Pack a Tote
   * @param packingList - the packing-list
   * @param orderData - the packing-list order data
   * @returns the observable for pack a tote
   */
  packTote$(
    packingList: PackingList,
    orderData: PackingListOrderData,
  ): Observable<PackingListPackData | undefined> {
    if (!orderData.action?.toteId || !orderData.order) {
      this.notify({ code: 'TOTE_NOT_FOUND' })
      return of(undefined)
    }

    return this.packingListsService
      .scanTote$(packingList._id, orderData.action.toteId)
      .pipe(
        map((updatedPacking) => {
          const order = orderData.order
          const productId = orderData.action?.productId
          const action = updatedPacking.actions.find(
            (a) => a.productId === productId && a.orderId === order?._id,
          )
          return {
            ...orderData,
            packingList: updatedPacking,
            action,
          }
        }),
      )
  }

  /**
   * Print order of a packing-list
   * @param packingList - the packing-list
   * @param orderId - the order ID
   * @param orderPackages - the order packages
   * @param additionalPackages - the order additional packages
   * @param force - the force flag
   * @returns the observable for print the order
   */
  printOrder$(
    packingList: PackingList,
    orderId: string,
    orders: Order[],
    packages: OrderPackage[],
    additionalPackages?: OrderAdditionalPackage[],
    force = false,
  ): Observable<PackingListPackData> {
    return this.packingListsService
      .packOrder$(packingList._id, orderId, {
        packages,
        additionalPackages,
        force,
      })
      .pipe(
        switchMap((packingList) =>
          this.loadPackingPackData$(
            packingList,
            orders,
            orders.find((o) => o._id === orderId),
          ),
        ),
      )
  }

  // Utilities

  private loadPickingTotes$(pickingList: PickingList): Observable<Location[]> {
    const ids = pickingList.totes.map((t) => t._id)
    if (!ids.length) {
      return of([])
    }
    return this.locationsService.list$(
      {
        _id: ids,
      },
      true,
    )
  }

  private loadPickingOrders$(pickingList: PickingList): Observable<Order[]> {
    return this.ordersService.list$({ pickingListId: pickingList._id }, true)
  }

  private loadPickingUser$(
    pickingList: PickingList,
  ): Observable<User | undefined> {
    const userId = getPickingPickedByUser(pickingList)
    return userId ? this.usersService.read$(userId) : of(undefined)
  }

  private loadPackingOrderProducts$(
    packingList: PackingList,
    orderId: string,
  ): Observable<Product[]> {
    const productIds = packingList.actions
      .filter((a) => a.orderId === orderId)
      .map((a) => a.productId)

    if (!productIds.length) {
      return of([])
    }

    return this.productsService.list$({ _id: productIds }, true).pipe(
      switchMap((products) => {
        const parentIds = products
          .map((p) => p.parentId)
          .filter(
            (parentId): parentId is string =>
              !!parentId && !productIds.includes(parentId),
          )

        if (!parentIds.length) {
          return of(products)
        }

        return this.productsService
          .list$({ _id: parentIds }, true)
          .pipe(map((parentProducts) => [...products, ...parentProducts]))
      }),
    )
  }

  private checkProductPackages(
    packingList: PackingList,
    product: Product,
    barcode: string,
    packages: OrderPackage[],
    orderId: string,
  ): OrderPackage[] {
    const wrapper = getProductWrapperByBarcode(product, barcode)

    if (!wrapper?.isSelfShipping) {
      return packages
    }

    const weightToPack = getPackingActionWeightToPack(
      packingList,
      orderId,
      packages,
    )

    return addOrderWrapperPackage(packages, wrapper, weightToPack)
  }

  notify(options: {
    code?: PackingListNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parsePackingListNotification(options.code),
    )
  }

  async alert(
    code: PackingListModalCode,
    data?: {
      pickingListName?: string
      user?: User
      packCode?: string
      packWeight?: string
    },
  ): Promise<ModalResponse> {
    const settings = parsePackingListModal(code, data)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
